<template>
  <div>
    <NuxtLayout
      name="default"
      :isPrimaryNavbarSticky="false"
      hero-section-id="admission-hero"
    >
      <HeroStandardApollo
        :title="$page?.whatItCosts.title!"
        :bg-image-source="$page?.whatItCosts.heroImage!"
        :gradient-opacity="0.75"
      >
        <template #below-text>
          <button
            role="button"
            class="btn btn-secondary fw-bold fs-medium py-space-xxs px-space-xxs px-md-space-xs d-inline-block"
            data-cy="use-our-tuition-calculator-button"
            @click="scrollToCalculatorHero"
          >
            {{ $page?.whatItCosts.heroLinkText }}
          </button>
        </template>
      </HeroStandardApollo>
      <!-- sticky navbar -->
      <NavbarStickyAtlas
        :nav-items="PAGE_CONSTANTS.NAV_ITEMS"
        title="On this page:"
        id="what-it-cost-sticky-nav"
      >
        <template #stuckMenu>
          <div
            class="d-flex justify-content-end flex-direction-row mb-space-xxxs"
          >
            <ButtonApplyNow
              type="modal"
              event-region="navbar"
              event-section="sticky navbar"
            ></ButtonApplyNow>
            <button
              class="btn btn-secondary fs-medium fw-bold ms-space-sm"
              @click="smoothScrollTo('asuo-rfi-section')"
            >
              Request info
            </button>
          </div>
        </template>
      </NavbarStickyAtlas>
      <!-- sectionApollo -->
      <SectionApollo
        :image="$page?.whatItCosts.introductionImage!"
        :title="$page?.whatItCosts.introductionTitle!"
        id="cost-calculator-overview"
        image-position="right"
      >
        <div
          v-html="$page?.whatItCosts.introductionDescription"
          class="mb-space-md"
        ></div>
        <button
          class="btn btn-secondary fw-bold fs-small p-space-xxs px-md-space-xs py-md-0"
          @click="scrollToCalculator"
        >
          <FontAwesomeIcon
            icon="calculator"
            class="me-space-xxs"
            role="presentation"
          />
          {{ $page?.whatItCosts.introductionLinkText }}
        </button>
      </SectionApollo>
      <section
        class="bg-white pb-space-md pb-md-space-lg pb-lg-space-xl px-space-xs"
      >
        <div class="container bg-light-2">
          <div class="row">
            <div class="col-12 px-0">
              <div class="pt-space-md pb-space-sm px-space-sm">
                <div class="bg-secondary title-underline mb-space-sm"></div>
                <div
                  class="fs-small"
                  v-html="$page?.whatItCosts.introductionDisclaimer"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <!-- cost calculator starts -->
      <SectionParallaxAtlas
        id="cost-calculator"
        :bg-image-source="$page?.whatItCosts.tcBackgroundImage!"
        :title="$page?.whatItCosts.tcTitle!"
      >
        <template #text>
          <div class="row" ref="rCostCalculator">
            <div class="col-12 col-lg-8 pb-space-sm pb-lg-space-md">
              <div
                v-html="$page?.whatItCosts.tcDescription!"
                class="mb-space-sm mb-lg-space-md tc-description"
              ></div>
              <div class="bg-secondary title-underline-small"></div>
            </div>
          </div>
          <div class="row">
            <div class="col-12 col-lg-5">
              <div class="bg-light-3 px-space-sm py-space-md py-lg-space-lg">
                <form
                  novalidate
                  @submit.prevent="updateCalculator"
                  id="tuition-calculator"
                  data-cy="tuition-calculator-form"
                >
                  <div
                    class="form-group mb-space-sm"
                    id="calc-resident-type-group"
                  >
                    <label
                      for="calc-resident-type_typeahead__search"
                      class="form-label fw-bold mb-space-xxs"
                      >What's your current residency?</label
                    >
                    <TypeAheadSelect
                      id="calc-resident-type"
                      :options="currentResidencyOptions"
                      :clearable="false"
                      @changed="handleResidencySelectChange"
                      v-model="form.currentResidencyOption"
                      data-cy="“tuition-calculator-Resident-dropdown”"
                    />
                  </div>
                  <div
                    class="form-group mb-space-sm"
                    id="calc-degree-type-group"
                  >
                    <label
                      for="calc-degree-type_typeahead__search"
                      class="form-label fw-bold mb-space-xxs"
                      >What's your student status?</label
                    >
                    <TypeAheadSelect
                      id="calc-degree-type"
                      :options="degreeTypeOptions"
                      :clearable="false"
                      placeholder="Select a degree type"
                      @changed="handleDegreeTypeChange"
                      v-model="form.degreeType"
                      data-cy="“tuition-calculator-degree-dropdown”"
                    />
                  </div>
                  <div
                    class="form-group mb-space-sm"
                    v-if="!isNonDegreeSelected"
                    id="calc-program-type-group"
                  >
                    <label
                      for="calc-program-type"
                      class="form-label fw-bold mb-space-xxs"
                      >Choose a program</label
                    >
                    <TypeAheadSelect
                      alpha-sort
                      id="calc-program-type"
                      :options="programOptions"
                      :clearable="false"
                      placeholder="Select a program type"
                      @changed="handleChooseProgramChange"
                      v-model="form.program"
                      aria-describedby="program-error"
                      data-cy="“tuition-calculator-program-dropdown”"
                    />
                    <p
                      class="text-danger"
                      v-if="form.validate.program.hasError"
                      id="program-error"
                      role="alert"
                      aria-live="assertive"
                    >
                      Please select a program
                    </p>
                  </div>
                  <div
                    v-if="displayAcademicYear"
                    class="form-group mb-space-sm"
                    id="calc-program-year-group"
                  >
                    <label
                      for="calc-program-year"
                      class="form-label fw-bold mb-space-xxs"
                      >Choose an academic year</label
                    >
                    <TypeAheadSelect
                      id="calc-program-year"
                      :options="academicYearOptions"
                      :clearable="false"
                      @changed="handleAcademicYearChange"
                      v-model="form.academicYear"
                      data-cy="“tuition-calculator-academic-year-dropdown”"
                    />
                  </div>
                  <div class="row">
                    <div class="col-12">
                      <button
                        id="calculate-btn"
                        type="submit"
                        :disabled="isCalculateDisabled"
                        class="btn btn-dark-3 fw-bold fs-medium py-space-xs px-space-md mb-space-sm mb-lg-0"
                        data-cy="tuition-calculator-calculate-button"
                      >
                        Calculate
                      </button>
                    </div>
                  </div>
                </form>
                <div class="d-block d-lg-none">
                  <p class="fs-xs text-center mb-0">
                    Swipe down to see results after you click “Calculate”
                  </p>
                </div>
              </div>
              <div
                class="fs-xs pr-space-xs pt-lg-space-md my-space-sm mt-lg-space-md mb-lg-0 bt-lg-space"
                v-html="$page?.whatItCosts.tcFootnote!"
              ></div>
            </div>
            <div class="col-12 col-lg-7" id="cost-calculator-results">
              <div>
                <div
                  v-if="displayCostCalculator"
                  class="bg-white py-space-sm py-md-space-md px-space-xs px-md-space-md"
                >
                  <!-- handles cost calculator component error -->
                  <div v-if="calcError" class="error-message-block p-space-md">
                    <div class="alert alert-danger mb-0" role="alert">
                      Oops! We are currently experiencing issues retrieving cost
                      information for this program. We apologize for the
                      inconvenience, please try back later.
                    </div>
                  </div>
                  <template v-else>
                    <CostCalculator
                      v-if="selectedProgramData"
                      :cost="selectedProgramData"
                      :calculator-title="PAGE_CONSTANTS.CALCULATOR_TITLE"
                      @calc-error="handleCalculatorError"
                    ></CostCalculator>
                  </template>
                </div>
                <template v-else>
                  <div class="position-relative">
                    <img
                      :src="$page?.whatItCosts.tcImage!"
                      alt="tuition calculator placeholder image"
                      class="img-fluid"
                    />
                    <div class="position-absolute calc-text">
                      <p class="fs-xl fw-bold position-relative text-center">
                        Plug in your information and choose 'Calculate'.
                      </p>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </template>
        <template #footer>
          <div class="row">
            <div class="col-12">
              <div
                class="bg-secondary title-underline mb-space-sm mt-space-md mt-lg-space-lg"
              ></div>
              <div
                v-html="$page?.whatItCosts.tcDisclaimer"
                class="fs-small"
              ></div>
            </div>
          </div>
        </template>
      </SectionParallaxAtlas>
      <!-- cost calculator ends -->

      <section
        class="video bg-dark-3 py-space-md py-md-space-lg py-lg-space-xxl"
      >
        <div class="container">
          <div class="row gx-0">
            <div class="col-12">
              <VideoModalAtlas
                class="video"
                :video-source="`https://www.youtube.com/embed/${$page?.whatItCosts.video?.youtubeId}`"
                :bg-image-source="$page?.whatItCosts.heroImage!"
                :title="$page?.whatItCosts.video?.title!"
                title-variant="white"
                :display-border="false"
                @vma-open="
                  handleVideoModal(
                    'open',
                    $page?.whatItCosts.video?.title!,
                    'play button'
                  )
                "
                @vma-close="
                  handleVideoModal(
                    'close',
                    $page?.whatItCosts.video?.title!,
                    'close button'
                  )
                "
              >
                <div v-html="$page?.whatItCosts?.video?.description!"></div>
              </VideoModalAtlas>
            </div>
          </div>
        </div>
      </section>
      <!-- cost collection faq -->
      <section
        class="pt-space-lg pb-space-md pt-md-space-lg pb-md-space-sm py-lg-space-xxl position-relative faq-bg"
        :style="{ '--faq-bg': `url(${$page?.whatItCosts.faqBgImage})` }"
        id="cost-calculator-faq"
      >
        <div class="container">
          <div class="row">
            <div class="col-12">
              <h2 class="h2-medium pb-space-sm">
                {{ $page?.whatItCosts.faqTitle }}
              </h2>
              <div class="bg-secondary title-underline mb-space-md"></div>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <div
                v-for="(item, i) in $page?.whatItCosts.faqList"
                :key="i"
                class="mb-space-xs bg-light-1"
              >
                <CollapseItem
                  :collapse-id="`faq-collapse-${i}`"
                  :display-number="false"
                  :title="item?.title!"
                  title-size="large"
                  @collapseShown="
                    handleCollapseClickEvent('open', item?.title!)
                  "
                  @collapseHidden="
                    handleCollapseClickEvent('close', item?.title!)
                  "
                >
                  <div v-html="item?.description"></div>
                </CollapseItem>
              </div>
            </div>
          </div>
        </div>
      </section>
      <!-- card-icon section -->
      <section
        class="bg-light-2 pt-space-lg pb-space-md pt-md-space-lg pb-md-space-sm py-lg-space-xxl position-relative"
        id="cost-calculator-contact"
      >
        <div class="container">
          <div class="row">
            <div class="col-12 col-md-9">
              <h2 class="h2-medium pb-space-sm">
                {{ $page?.whatItCosts.connectTitle }}
              </h2>
              <div
                class="pb-space-md"
                v-html="$page?.whatItCosts.connectDescription"
              ></div>
            </div>
            <div class="row">
              <div
                class="col-12 col-md-6 col-lg-3 mb-space-sm"
                v-for="(item, index) in $page?.whatItCosts.connectCard"
                :key="index"
              >
                <CardIcon
                  :icon-source="item?.icon!"
                  :icon-alt="item?.title!"
                  :title="item?.title!"
                  :cta-as-button="true"
                  cta-button-variant="dark-3"
                  :cta-link="item?.link?.uri!"
                  cta-text-weight="bold"
                  cta-text-variant="white"
                  cta-text-size="small"
                  :cta-text="item?.link?.text!"
                  :clickable-card="false"
                  class="card-deck h-100"
                  @ctaClick="handleCardClick(item?.link?.text!)"
                >
                  <div v-html="item?.text!" class="mb-space-sm"></div>
                </CardIcon>
              </div>
            </div>
          </div>
        </div>
      </section>
      <!-- SectionRfiMultiStep -->
      <SectionRfiMultiStep :bg-image="RfiBgImage"></SectionRfiMultiStep>
    </NuxtLayout>
  </div>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import { CardIcon } from "@rds-vue-ui/card-icon";
import { CollapseItem } from "@rds-vue-ui/collapse-item";
import { HeroStandardApollo } from "@rds-vue-ui/hero-standard-apollo";
import { NavbarStickyAtlas } from "@rds-vue-ui/navbar-sticky-atlas";
import { SectionApollo } from "@rds-vue-ui/section-apollo";
import { SectionParallaxAtlas } from "@rds-vue-ui/section-parallax-atlas";
import { VideoModalAtlas } from "@rds-vue-ui/video-modal-atlas";
import { useWindowSize } from "@vueuse/core";
import RfiBgImage from "~/assets/img/rfi-bg-overlay.png";
import ButtonApplyNow from "~/components/nav/ButtonApplyNow.vue";
import SectionRfiMultiStep from "~/components/rfi/SectionRfiMultiStep.vue";
import TypeAheadSelect, { OptionType } from "~/components/TypeAheadSelect.vue";
import CostCalculator from "~/components/what-it-costs-page/CostCalculator.vue";
import {
  Category,
  DegreeSeekingProgram,
  WhatItCosts,
} from "~/server/resolvers/resolvers-types";
import CostService from "~/src/services/CostService";

import {
  AsuTuitionApiCostObject,
  FormattedProgramCostCalc,
} from "~/types/types";
interface IPage {
  whatItCosts: WhatItCosts;
  allCategory: Category[];
  allCostStudentCategory: Category[];
  allDegreeSeekingProgram: DegreeSeekingProgram[];
}
const siteUrl = useRuntimeConfig().public.siteUrl;
const schemaService = useSchemaService();
/*
********************************************
Page quries
********************************************
*/
const $page = await usePageQuery<IPage>(
  `#graphql
      query WhatItCosts($whatItCostsId: ID!) {
        whatItCosts(id: $whatItCostsId) {
          id
          metaTitle
          metaDescription
          title
          heroImage
          heroLinkText
          detailPage
          introductionImage
          introductionTitle
          introductionLinkText
          introductionDescription
          introductionDisclaimer
          tcBackgroundImage
          tcImage
          tcTitle
          tcDescription
          tcDisclaimer
          tcFootnote
          video {
            id
            youtubeId
            title
            date
            duration
            description
            uploadDate
            imageSource
          }
          faqTitle
          faqBgImage
          faqList {
            title
            description
          }
          connectTitle
          connectDescription
          connectCard {
            title
            icon
            text
            link {
              text
              uri
              target
            }
          }
        }
        allCategory {
          title
        }
        allCostStudentCategory {
          title
        }
        allDegreeSeekingProgram {
          id
          code
          title
          alternateTitle
          detailPage
          category {
            title
          }
          interestAreas {
            title
          }
          costOverride
          costAcademicYear
          latestAcademicYear
          programCode
          resProgramFeeCode
          noresProgramFeeCode
          costCollection {
            amount
          }
        }
      }
      `,
  {
    whatItCostsId: "7ac75f3e-9fc9-4044-b4af-a84833e2c88f",
  },
  { key: "what-it-costs-index" }
);

/*
********************************************
Page meta tags
********************************************
*/
definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    ldjson: ["innerHTML"],
  },
});
useHead({
  title: $page?.whatItCosts.metaTitle,
  meta: [
    {
      name: "Title",
      content: $page?.whatItCosts.metaTitle,
    },
    {
      name: "Description",
      content: $page?.whatItCosts.metaDescription,
    },
    ...useUtilityService().generateSocialTags(
      $page?.whatItCosts.metaTitle as string,
      $page?.whatItCosts.metaDescription as string,
      $page?.whatItCosts.heroImage as string
    ),
  ],
  link: [
    {
      rel: "canonical",
      href: useUtilityService().formatCanonicalUrl(
        `${siteUrl}${$page?.whatItCosts.detailPage}`
      ),
    },
  ],
  script: [schemaService.generateWhatItCostsPageSchema($page?.whatItCosts)],
});

const { width } = useWindowSize();

const scrollToCalculatorHero = () => {
  rCostCalculator.value?.scrollIntoView({ behavior: "smooth", block: "start" });
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    $page?.whatItCosts.title?.toLowerCase()!,
    $page?.whatItCosts.heroLinkText?.toLowerCase()!,
    "button - icon"
  );
};

const PAGE_CONSTANTS = {
  NAV_ITEMS: {
    "cost-calculator-overview": "Overview",
    "cost-calculator": "Calculator",
    "cost-calculator-faq": "FAQ",
    "cost-calculator-contact": "Contact",
  },
  ARIZONA_RESIDENT: "Arizona resident",
  NON_RESIDENT: "Nonresident",
  UG_NON_DEGREE: "Undergraduate (Nondegree)",
  CALCULATOR_TITLE: "Your tuition estimate",
};

const smoothScrollTo = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }
  analyticsComposable.trackEvent({
    event: "link",
    name: "onclick",
    action: "click",
    type: "internal link",
    region: "navbar",
    section: "sticky navbar",
    text: "request info",
    component: "sticky navbar",
  });
};
const rCostCalculator = ref<HTMLElement | null>(null);
const scrollToCalculator = () => {
  rCostCalculator.value?.scrollIntoView({ behavior: "smooth" });
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    $page?.whatItCosts.title?.toLowerCase()!,
    $page?.whatItCosts.introductionLinkText?.toLowerCase()!,
    "button - icon"
  );
};

const handleVideoModal = (action: string, section: string, text: string) => {
  analyticsComposable.trackModalEvent(
    "onclick",
    action.toLowerCase(),
    "click",
    "main content",
    section.toLowerCase(),
    text.toLowerCase(),
    "video - modal"
  );
};

const handleCollapseClickEvent = (action: string, text: string) => {
  analyticsComposable.trackCollapseEvent(
    "onclick",
    action.toLowerCase(),
    "click",
    "main content",
    "faq about tuition",
    text.toLowerCase()
  );
};

const handleCardClick = (text: string) => {
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    $page?.whatItCosts?.connectTitle?.toLowerCase() || "NA",
    text.toLowerCase(),
    "card - icon"
  );
};

const displayCostCalculator = ref(false);
const calcError = ref(false);

const currentResidencyOptions = ref([
  {
    value: PAGE_CONSTANTS.ARIZONA_RESIDENT,
    text: PAGE_CONSTANTS.ARIZONA_RESIDENT,
  },
  { value: PAGE_CONSTANTS.NON_RESIDENT, text: PAGE_CONSTANTS.NON_RESIDENT },
]);

type ResidencyOptionType = {
  text: "Arizona resident" | "Nonresident";
  value: "Arizona resident" | "Nonresident";
};

const form = reactive({
  currentResidencyOption: currentResidencyOptions
    .value[0] as ResidencyOptionType,
  degreeType: null as OptionType | null,
  program: null as OptionType | null,
  academicYear: null as OptionType | null,
  validate: {
    currentResidencyOption: {
      hasError: false,
    },
    degreeType: {
      hasError: false,
    },
    program: {
      hasError: false,
    },
    academicYear: {
      hasError: false,
    },
  },
});

const isMounted = ref<boolean>(false);

const envProcess = useRuntimeConfig().public;

const allCategories = useRfiService().getAllFormattedCategories(
  $page?.allCategory!
);

const allStudentCategories = CostService.getAllFormattedCostStudentCategories(
  $page?.allCostStudentCategory!
);

const allPrograms = CostService.generateFormattedProgramCollection(
  $page?.allDegreeSeekingProgram!
);

const allProgramMap = useRfiService().generateAllProgramMap(
  allPrograms as DegreeSeekingProgram[]
);

const degreeTypeOptions =
  CostService.getDegreeTypeOptions(allStudentCategories);

const programOptions = ref<OptionType[]>([]);

const categoryProgramsMap = CostService.generateCategoryProgramsMap(
  allCategories,
  allPrograms
);

const isNonDegreeSelected = computed(() => {
  if (!form.degreeType) return false;
  return form.degreeType?.value === PAGE_CONSTANTS.UG_NON_DEGREE;
});

const isCalculateDisabled = computed<boolean>(() => {
  if (isNonDegreeSelected.value == true) {
    return form.degreeType === null;
  }
  return !form.program;
});

const displayAcademicYear = ref<boolean>(true);

const handleDegreeTypeChange = (degreeType: OptionType | null) => {
  displayAcademicYear.value = true;
  let degreeText = "";
  let programs: any[] = [];
  if (degreeType && !isNonDegreeSelected.value && degreeType.value !== null) {
    degreeText = degreeType.text;
    programs = [...categoryProgramsMap[degreeType?.value]];
    const isProgramFound = programs.find(
      (program) => program.id === form.program?.value
    );
    if (!isProgramFound && form.program) {
      form.program = null;
      form.validate.program.hasError = true;
    }
    setProgramOptions(programs);
  } else {
    form.program = null;
    if (!isNonDegreeSelected.value) {
      form.validate.program.hasError = false;
    } else {
      form.validate.program.hasError = true;
      degreeText = "undergraduate (nondegree)";
    }
    setProgramOptions(allPrograms);
  }
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "what's your student status?",
    "main content",
    "asu online tuition calculator",
    degreeText !== "" ? degreeText.toLowerCase() : "select a degree type",
    "tuition calculator"
  );
};

const setProgramOptions = (programs: FormattedProgramCostCalc[]) => {
  programOptions.value = CostService.getProgramOptions(programs).sort(function (
    a,
    b
  ) {
    if (a.text.toLowerCase() < b.text.toLowerCase()) {
      return -1;
    }
    if (a.text.toLowerCase() > b.text.toLowerCase()) {
      return 1;
    }
    return 0;
  });
};

const handleResidencySelectChange = (value: OptionType | null) => {
  if (value !== null) {
    const text = value.text ? value.text.toLowerCase() : "select resident type";
    analyticsComposable.trackSelectEvent(
      "onclick",
      "click",
      "what's your current residency?",
      "main content",
      "asu online tuition calculator",
      text,
      "tuition calculator"
    );
  }
};

const selectedProgramData = ref<AsuTuitionApiCostObject>();

const handleCalculatorError = () => {
  calcError.value = true;
};

const currentYear = ref<string>();
const academicYearOptions = ref<OptionType[]>([]);

const formatAcademicYearOption = (year: number) => {
  return {
    text: `${year - 1}-${year}`,
    value: year.toString(),
  };
};

const getCurrentAcademicYear = async () => {
  if (!$page) return;
  const cYear = $page.allDegreeSeekingProgram[0].costAcademicYear;
  const nYear = $page.allDegreeSeekingProgram[0].latestAcademicYear;
  currentYear.value = cYear;
  const parsedCurrentYear = parseInt(cYear);
  const parsedNewesttAcademicYear = parseInt(nYear);
  form.academicYear = formatAcademicYearOption(parsedCurrentYear);
  for (let i = 0; i < 2; i++) {
    academicYearOptions.value.push(
      formatAcademicYearOption(parsedCurrentYear - i)
    );
  }
  if (parsedNewesttAcademicYear > parsedCurrentYear) {
    academicYearOptions.value.unshift(
      formatAcademicYearOption(parsedNewesttAcademicYear)
    );
  }
};

const updateCalculator = async (event: Event) => {
  event.preventDefault();
  displayCostCalculator.value = false;
  if (isNonDegreeSelected.value) {
    const costUrlTemplate = {
      [PAGE_CONSTANTS.ARIZONA_RESIDENT]: `?acad_career=UGRDN&honors=0&admit_term=&include_summer=0&campus=ONLNE&acad_prog=&program_fee=&residency=res`,
      [PAGE_CONSTANTS.NON_RESIDENT]: `?acad_career=UGRDN&honors=0&admit_term=&include_summer=0&campus=ONLNE&acad_prog=&program_fee=&residency=nores`,
    };
    const currentYearStr: string = form.academicYear?.value
      ? `&acad_year=${form.academicYear.value}  `
      : "";
    const prepareUrl = `${envProcess.asuoApiBaseUrl}${
      envProcess.asuoApiCostInfoPath
    }${costUrlTemplate[form.currentResidencyOption.value]}${currentYearStr}`;
    // @ts-ignore
    selectedProgramData.value = {
      costUrl: prepareUrl,
      isNondegree: isNonDegreeSelected.value,
      category: "Undergraduate",
      override: false,
      academicYear: form.academicYear?.value!,
    };
  } else {
    selectedProgramData.value = CostService.populateCostObject(
      allProgramMap[form.program?.value!] as FormattedProgramCostCalc,
      form.currentResidencyOption.value,
      form.academicYear?.value!
    );
  }
  await nextTick();
  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    "asu online tuition calculator",
    "calculate",
    "tuition calculator"
  );
  displayCostCalculator.value = true;
  if (width.value < 992) {
    scrollToTarget("cost-calculator-results", {
      offsetElement: "what-it-cost-sticky-nav",
    });
  }
};

const handleChooseProgramChange = (val: OptionType | null) => {
  if (val !== null) {
    form.validate.program.hasError = false;
    displayAcademicYear.value =
      !allProgramMap[form.program?.value!].costOverride!;
    analyticsComposable.trackSelectEvent(
      "onclick",
      "click",
      "choose a program",
      "main content",
      "asu online tuition calculator",
      val.text.toLowerCase(),
      "tuition calculator"
    );
  }
};
const handleAcademicYearChange = (val: OptionType | null) => {
  if (val !== null && isMounted.value === true) {
    form.validate.academicYear.hasError = false;
    analyticsComposable.trackSelectEvent(
      "onclick",
      "click",
      "choose an academic year",
      "main content",
      "asu online tuition calculator",
      val.text.toLowerCase(),
      "tuition calculator"
    );
  }
};

onMounted(async () => {
  await getCurrentAcademicYear();
  setProgramOptions(allPrograms);
  isMounted.value = true;
});
</script>

<style lang="scss" scoped>
:deep(.video) {
  .card-body {
    background-color: var(--rds-dark-3);
    color: #fafafa !important;
  }
}
:deep(.card-icon-height) {
  height: 62px;
}
.calc-text {
  top: 100px;
  width: 100%;
}
.calculator-top {
  margin-top: -48px;
}
.title-underline {
  width: 64px;
  height: 10px;
}
.title-underline-small {
  width: 48px;
  height: 4px;
}
.faq-bg {
  background-image: var(--faq-bg);
  position: relative;
  background-size: auto;
  background-position: top;
}
.or-decorator {
  position: relative;

  &::before {
    content: "";
    position: absolute;
    width: 45px;
    height: 1px;
    background-color: var(--rds-dark-1, #747474);
    transform: translateX(18px);
    top: 50%;
  }
  &::after {
    content: "";
    position: absolute;
    width: 45px;
    height: 1px;
    background-color: var(--rds-dark-1, #747474);
    transform: translateX(calc(-100% - 18px));
    top: 50%;
  }
}

/* Medium devices (tablets, 768px and up) */
@media (min-width: 768px) {
  .calc-text {
    left: 30%;
    width: 270px;
  }
}

/* Large devices (desktops, 992px and up) */
@media (min-width: 992px) {
  .calculator-top {
    margin-top: -128px;
  }
  .calc-text {
    top: 200px;
  }
  .bt-lg-space {
    border-top: var(--rds-light-4, #d0d0d0) 1px solid;
  }
}
</style>
